import { defineRecipe } from "@chakra-ui/react";

// v2 Button baseStyle defaults (v3 differs on weight, line-height, focus ring, disabled).
const buttonBase = {
  fontWeight: "semibold",
  borderRadius: "md",
  lineHeight: "1.2",
  gap: "2",
  // v2 parity: v3's default button recipe forces `flexShrink: 0`, which stops
  // buttons shrinking inside constrained flex rows. v2 left it at the CSS default.
  flexShrink: "1",
  transitionProperty: "common",
  transitionDuration: "normal",
  _focusVisible: {
    boxShadow: "outline"
  },
  _disabled: {
    cursor: "not-allowed",
    boxShadow: "none"
  }
};

const buttonVariantStyles = {
  primary: {
    bg: "brand.500",
    color: "white",
    _hover: { bg: "brand.700 !important" },
    _active: { bg: "brand.800 !important" },
    _disabled: { opacity: 0.5 }
  },
  secondary: {
    bg: "white",
    color: "gray.800",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "gray.800",
    _hover: { bg: "brand.50" },
    _active: { bg: "brand.100" },
    _disabled: { color: "gray.800", opacity: 0.5 }
  },
  outline: {
    bg: "transparent",
    color: "gray.800",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "gray.800",
    _hover: { bg: "gray.50" },
    _active: { bg: "gray.100" },
    _disabled: { opacity: 0.5 }
  },
  /** v2 Chakra `solid` — neutral surface; FAB triggers override `bg` explicitly. */
  solid: {
    bg: "gray.100",
    color: "gray.800",
    _hover: { bg: "gray.200" },
    _active: { bg: "gray.300" },
    _disabled: { opacity: 0.5 }
  },
  // Resolves bg/hover/active from the caller's `colorPalette` (social login
  // buttons); the default `primary` variant hard-codes brand and ignores it.
  social: {
    bg: "colorPalette.500",
    color: "white",
    _hover: { bg: "colorPalette.600" },
    _active: { bg: "colorPalette.700" },
    _disabled: { opacity: 0.5 }
  },
  tertiary: {
    bg: "white",
    color: "gray.800",
    _hover: { bg: "gray.100" },
    _active: { bg: "gray.200" },
    _disabled: { opacity: 0.5 },
    "&[data-active], &[aria-pressed=true]": {
      bg: "gray.200",
      color: "gray.800"
    }
  },
  "emoji-row": {
    bg: "white",
    borderLeftWidth: "4px",
    borderLeftStyle: "solid",
    borderLeftColor: "transparent",
    _hover: {
      bg: "white",
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      "@media (hover: hover) and (pointer: fine)": {
        "& svg": { color: "yellow.200" },
        "& p": { fontWeight: "semibold" }
      }
    },
    _focus: {
      bg: "white",
      "& svg": { color: "yellow.200" },
      "& p": { fontWeight: "semibold" }
    },
    _active: {
      bg: "white",
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderLeftColor: "brand.500",
      borderBottomColor: "transparent",
      "& svg": { color: "yellow.300" },
      "& p": { fontWeight: "bold" }
    }
  },
  "emoji-column": {
    bg: "white",
    borderBottomWidth: "4px",
    borderBottomStyle: "solid",
    borderBottomColor: "transparent",
    _hover: {
      bg: "white",
      borderTopLeftRadius: "md",
      borderBottomRightRadius: 0,
      "@media (hover: hover) and (pointer: fine)": {
        "& svg": { color: "yellow.200" },
        "& p": { fontWeight: "semibold" }
      }
    },
    _focus: {
      bg: "white",
      "& svg": { color: "yellow.200" },
      "& p": { fontWeight: "semibold" }
    },
    _active: {
      bg: "white",
      borderTopLeftRadius: "md",
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,
      borderLeftColor: "transparent",
      borderBottomColor: "brand.500",
      "& svg": { color: "yellow.300" },
      "& p": { fontWeight: "bold" }
    }
  },
  link: {
    color: "brand.500",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    _disabled: { opacity: 0.5 }
  },
  "destructive-primary": {
    bg: "red.600",
    color: "white",
    _hover: { bg: "red.800" },
    _active: { bg: "red.900" },
    _disabled: { opacity: 0.5 }
  },
  "destructive-secondary": {
    bg: "white",
    color: "red.600",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "red.600",
    _hover: { bg: "red.50" },
    _active: { bg: "red.100" },
    _disabled: { opacity: 0.5 }
  },
  "destructive-tertiary": {
    bg: "white",
    color: "red.600",
    _hover: { bg: "red.100" },
    _active: { bg: "red.200" },
    _disabled: { opacity: 0.5 }
  },
  "destructive-link": {
    bg: "white",
    color: "red.600",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    _disabled: { opacity: 0.5 }
  },
  ghost: {
    bg: "transparent",
    color: "inherit",
    fontWeight: "normal",
    _hover: {
      bg: "blackAlpha.100",
      _dark: { bg: "whiteAlpha.100" }
    },
    _active: {
      bg: "blackAlpha.200",
      _dark: { bg: "whiteAlpha.200" }
    },
    _disabled: { opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }
  },
  unstyled: {
    bg: "transparent",
    minW: "auto",
    h: "auto",
    p: 0,
    _hover: { bg: "transparent" },
    _active: { bg: "transparent" }
  }
} as const;

export const buttonRecipe = defineRecipe({
  className: "chakra-button",
  base: buttonBase,
  defaultVariants: {
    variant: "primary",
    size: "md"
  },
  variants: {
    variant: buttonVariantStyles,
    size: {
      sm: { h: "8", minW: "8", px: "3", fontSize: "sm" },
      md: { h: "10", minW: "10", px: "4", fontSize: "md" },
      lg: { h: "12", minW: "12", px: "6", fontSize: "lg" }
    }
  },
  // Compound variants merge after `size`, so this is where we strip the
  // button-shaped height/padding off link variants to render as inline text.
  compoundVariants: [
    {
      variant: "link",
      css: {
        h: "auto",
        minW: "auto",
        px: 0,
        py: 0,
        lineHeight: "normal",
        verticalAlign: "baseline"
      }
    },
    {
      variant: "destructive-link",
      css: {
        h: "auto",
        minW: "auto",
        px: 0,
        py: 0,
        lineHeight: "normal",
        verticalAlign: "baseline"
      }
    }
  ]
});

export const iconButtonRecipe = defineRecipe({
  className: "chakra-icon-button",
  base: {
    ...buttonBase,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    // `aspectRatio` is applied conditionally by the wrapper (round prop); setting
    // it here would force square dimensions on `round={false}` call sites.
    p: 0,
    px: 0,
    "& svg": {
      flexShrink: 0
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "md"
  },
  variants: {
    variant: buttonVariantStyles,
    size: {
      sm: { boxSize: "8", minW: "8", minH: "8", fontSize: "sm" },
      md: { boxSize: "10", minW: "10", minH: "10", fontSize: "md" },
      lg: { boxSize: "12", minW: "12", minH: "12", fontSize: "lg" }
    }
  }
});

export const inputFocusInvalidStyles = {
  focusVisibleRing: "none",
  outline: "0",
  _focusVisible: {
    borderWidth: "2px",
    borderColor: "blue.500",
    boxShadow: "0 0 0 1px {colors.blue.500}",
    _dark: {
      borderColor: "blue.200",
      boxShadow: "0 0 0 1px {colors.blue.200}"
    }
  },
  _invalid: {
    focusRingColor: "red.600",
    borderWidth: "2px",
    borderColor: "red.600",
    boxShadow: "0 0 0 1px {colors.red.600}",
    _dark: {
      focusRingColor: "red.300",
      borderColor: "red.300",
      boxShadow: "0 0 0 1px {colors.red.300}"
    },
    _focusVisible: {
      focusRingColor: "red.600",
      borderWidth: "2px",
      borderColor: "red.600",
      boxShadow: "0 0 0 1px {colors.red.600}",
      _dark: {
        focusRingColor: "red.300",
        borderColor: "red.300",
        boxShadow: "0 0 0 1px {colors.red.300}"
      }
    }
  }
};

const inputOutlineVariant = {
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "gray.500",
  color: "gray.700",
  fontWeight: "normal",
  lineHeight: "133%",
  ...inputFocusInvalidStyles,
  _dark: { color: "gray.100" },
  _hover: {
    borderColor: "gray.600",
    _dark: { borderColor: "whiteAlpha.400" }
  },
  _placeholder: {
    fontWeight: "normal",
    fontSize: "0.875rem",
    lineHeight: "133%",
    color: "gray.700"
  }
};

export const inputRecipe = defineRecipe({
  className: "chakra-input",
  base: {
    width: "100%",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "gray.500",
    focusVisibleRing: "none",
    outline: "0"
  },
  defaultVariants: {
    variant: "outline",
    size: "md"
  },
  variants: {
    variant: {
      outline: inputOutlineVariant
    },
    size: {
      sm: { h: "8", fontSize: "sm", px: "3" },
      md: { h: "10", fontSize: "md", px: "4" },
      lg: { h: "12", fontSize: "lg", px: "4" }
    }
  }
});

export const textareaRecipe = defineRecipe({
  className: "chakra-textarea",
  base: {
    width: "100%",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "gray.500",
    minH: "20",
    verticalAlign: "top",
    focusVisibleRing: "none",
    outline: "0"
  },
  defaultVariants: {
    variant: "outline",
    size: "md"
  },
  variants: {
    variant: {
      outline: inputOutlineVariant
    },
    size: {
      sm: { fontSize: "sm", px: "3", py: "2" },
      md: { fontSize: "md", px: "4", py: "2" },
      lg: { fontSize: "lg", px: "4", py: "3" }
    }
  }
});

export const headingRecipe = defineRecipe({
  className: "chakra-heading",
  base: {
    fontFamily: "heading",
    fontWeight: "heading"
  },
  variants: {
    size: {
      xs: { fontSize: "sm", lineHeight: "1.2" },
      sm: { fontSize: "md", lineHeight: "1.2" },
      md: { fontSize: "xl", lineHeight: "1.2" },
      lg: {
        fontSize: { base: "2xl", lg: "3xl" },
        lineHeight: { base: "1.33", lg: "1.2" }
      },
      xl: {
        // v2 bumps this h1 to 4xl at `md` (26em), not `xl` — keep that breakpoint.
        fontSize: { base: "3xl", md: "4xl" },
        lineHeight: { base: "1.33", md: "1.2" }
      },
      "2xl": {
        fontSize: { base: "4xl", lg: "5xl" },
        lineHeight: { base: "1.2", lg: "1" }
      },
      "3xl": {
        fontSize: { base: "5xl", lg: "5xl" },
        lineHeight: "1"
      },
      "4xl": {
        fontSize: { base: "5xl", lg: "5xl" },
        lineHeight: "1"
      }
    }
  },
  defaultVariants: {
    size: "xl"
  }
});

export const textRecipe = defineRecipe({
  className: "chakra-text",
  base: {
    fontFamily: "body",
    fontWeight: "body"
  },
  variants: {
    size: {
      xs: { fontSize: "xs" },
      sm: { fontSize: "sm" },
      md: { fontSize: "md" },
      lg: { fontSize: "lg" },
      xl: { fontSize: "xl" }
    }
  }
  // No `defaultVariants.size` on purpose: un-sized Text must inherit its parent's
  // fontSize (v2 behaviour), not be forced to md.
});

// Deep-merges onto v3's built-in `skipNavLink` recipe, so the visually-hidden
// base and its focus reveal are inherited; only the metrics that shrank against
// v2's `SkipLink` style config are restated here.
export const skipNavLinkRecipe = defineRecipe({
  className: "chakra-skip-nav",
  base: {
    padding: "4",
    // v2 set no font size, letting the link inherit the 16px body text; v3's
    // default pins it to `sm`, so name the equivalent scale explicitly.
    textStyle: "md",
    borderRadius: "md",
    focusVisibleRing: "none",
    _focusVisible: {
      boxShadow: "outline"
    }
  }
});

export const recipes = {
  button: buttonRecipe,
  iconButton: iconButtonRecipe,
  input: inputRecipe,
  textarea: textareaRecipe,
  heading: headingRecipe,
  text: textRecipe,
  skipNavLink: skipNavLinkRecipe
};
